<template>
  <div class="collect-sessions-list w-100 i-px-12">
    <view-session-detail-modal
      v-if="selectedSession"
      :selected-session="selectedSession"
      :is-showable="isShowable"
      @hide-view-modal="hidePanel"
    />

    <div
      class="d-flex flex-column align-items-center collect-sessions-list__wrapper"
    >
      <div
        class="d-flex justify-content-between align-items-center i-mt-11 i-mb-14 w-100"
      >
        <div>
          <h4 class="i-text-heading-4 i-text-neutral-120 i-mb-1">
            Collect Sessions
          </h4>
          <p class="i-text-body-2 i-text-neutral-110">
            View the status of all your Collect sessions
          </p>
        </div>
        <i-button
          v-if="collectEnabled"
          text="Customize"
          @button-click="customizeRedirect()"
        ></i-button>
      </div>

      <div class="w-100 mb-2 mt-2">
        <b-table
          id="sessions-table"
          ref="sessions-table"
          data-testid="sessions-table"
          :busy="collectSessions.loading"
          responsive
          borderless
          :items="collectSessions.list"
          :fields="fields"
          class="i-table collect-sessions-table"
          :show-empty="true"
          primary-key="id"
          @row-clicked="showPanel"
        >
          <template #empty>
            <div class="d-flex flex-column">
              <h6>No sessions have been created so far</h6>
              <small
                >Create a new session using the 'Collect' button in any
                customer</small
              >
            </div>
          </template>

          <template #cell(session)="data">
            <div class="d-flex">
              <div
                class="icon-wrapper d-flex align-items-center justify-content-center i-mr-8"
              >
                <i-icon symbol="medium-file-request" :size="20"></i-icon>
              </div>
              <div
                class="d-flex flex-column session-main justify-content-center"
              >
                <p class="i-text-neutral-120 i-text-subheading-2">
                  {{ data.item.customer_name }}
                </p>
                <ul
                  class="collect-session-info-list i-m-0 d-flex i-text-body-2 i-text-neutral-100"
                >
                  <li v-if="data.item.customer_information_requests">
                    {{
                      requestLabel(
                        data.item.customer_information_requests,
                        'Information Request',
                      )
                    }}
                  </li>
                  <li v-if="data.item.document_requests">
                    {{
                      requestLabel(
                        data.item.document_requests,
                        'Document Request',
                      )
                    }}
                  </li>
                </ul>
              </div>
            </div>
          </template>

          <template #cell(status)="data">
            <div class="i-pill i-text-subheading-3" data-testid="completed-at">
              <span
                v-if="data.item.completed_at"
                class="success"
                :title="formatDateTime(data.item.completed_at, 'lll')"
              >
                Completed
              </span>
              <span v-else-if="data.item.expired" class="warning">Expired</span>
              <span v-else class="neutral">Pending</span>
            </div>
          </template>

          <template #cell(created)="data">
            <span class="d-block i-text-body-2 i-text-neutral-120">{{
              formatDateTime(data.item.created_at, 'lll')
            }}</span>
          </template>

          <template #table-busy>
            <i-spinner class="mx-auto i-my-16" size="xl"></i-spinner>
          </template>
        </b-table>

        <div
          v-if="!collectSessions.loading && collectSessions.pages > 1"
          class="i-mt-4"
        >
          <b-pagination
            id="collectSessions-list-pagination"
            :value="currentPage"
            :total-rows="collectSessions.total"
            :per-page="10"
            limit="3"
            aria-controls="sessions-table"
            class="i-pagination"
            @input="changePage"
          >
            <template #first-text
              ><span class="chevron">
                <i-icon
                  symbol="medium-chevron-left-double"
                  :size="20"
                ></i-icon></span
            ></template>
            <template #prev-text>
              <span class="chevron">
                <i-icon symbol="medium-chevron-left" :size="20"></i-icon>
              </span>
            </template>
            <template #next-text>
              <span class="chevron">
                <i-icon symbol="medium-chevron-right" :size="20"></i-icon>
              </span>
            </template>
            <template #last-text>
              <span class="chevron">
                <i-icon
                  symbol="medium-chevron-right-double"
                  :size="20"
                ></i-icon>
              </span>
            </template>
          </b-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ViewSessionDetailModal from '@/V2/collect/ViewSessionDetailModal';
import { iButton, iIcon, iSpinner } from '@inscribe/signal';
import { mapGetters, mapState, mapActions } from 'vuex';
import { formatDateTime } from '@/utils/date';

export default {
  components: {
    iButton,
    iIcon,
    iSpinner,
    ViewSessionDetailModal,
  },
  data() {
    return {
      show: false,
      selectedSession: null,
      fields: [
        {
          key: 'session',
          label: 'Session',
          thClass: 'session-column',
        },
        {
          key: 'status',
          label: 'Status',
          thClass: 'status-column',
        },
        {
          key: 'created',
          label: 'Date Created',
          thClass: 'created-column',
        },
      ],
    };
  },
  computed: {
    ...mapState({
      collectSessions: 'collectSessions',
    }),

    currentPage() {
      return this.$route.query.page || 1;
    },

    ...mapGetters({
      collectEnabled: 'collectEnabled',
    }),

    isShowable() {
      return this.show && !!this.selectedSession;
    },
  },

  watch: {
    '$route.query.page': {
      immediate: true,
      handler: function () {
        this.fetchCollectSessions();
      },
    },
  },

  methods: {
    ...mapActions({
      getSessions: 'getSessions',
    }),

    fetchCollectSessions() {
      this.getSessions({
        page: this.$route.query.page || 1,
      });
    },

    changePage(newPage) {
      this.$router.push({
        name: this.$route.name,
        query: {
          ...this.$route.query,
          page: newPage,
        },
      });
    },

    customizeRedirect() {
      this.$router.push({
        name: 'collect-config-settings-view',
      });
    },

    showPanel(session) {
      this.show = true;
      this.selectedSession = session;
    },

    hidePanel() {
      this.show = false;
      this.selectedSession = null;
    },

    requestLabel(requests, label) {
      return requests && requests.length === 1
        ? `${requests.length} ${label}`
        : `${requests.length} ${label}s`;
    },

    formatDateTime,
  },
};
</script>

<style lang="scss">
.collect-session-info-list {
  list-style-position: inside;
  padding-left: 0;

  li {
    list-style-type: none;
    padding: 0;
    margin: 0;

    &:not(:first-child)::before {
      content: '\2022';
      margin-left: 6px;
      margin-right: 1px;
    }
  }
}
</style>

<style lang="scss" scoped>
.collect-sessions-list {
  &__wrapper {
    max-width: 1200px;
    margin: 0 auto;

    @include respond-to('medium') {
      width: 100%;
      max-width: auto !important;
    }
  }

  ::v-deep .collect-sessions-table {
    &.i-table {
      .icon-wrapper {
        background: $light-primary-30;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        .i-icon {
          color: $light-primary-110 !important;
        }
      }
      .session-column {
        width: auto;
      }

      .status-column {
        width: 132px;
      }

      .created-column {
        width: 199px;
      }

      .actions-column {
        width: 68px;
      }
    }
  }
}
</style>
